import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Slider } from "@/components/ui/slider";
import { haversine, pathLength, polygonArea, fmtDist, fmtArea } from "@/lib/geo";
import {
  Crosshair, Lock, Unlock, Layers, Ruler, Play, Square, FolderOpen, Upload,
  X, Satellite, Map as MapIcon, Trash2, Eye, EyeOff, Plus, LogOut, Navigation,
  RotateCcw, ChevronDown, Gauge, Route,
} from "lucide-react";

const US_CENTER = { lat: 39.8283, lng: -98.5795 };
const SAT_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const STREET_URL = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const COVER_RADIUS = 7;

const dist2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// A Leaflet layer that renders the blueprint as a georeferenced, rotatable image.
// It participates in Leaflet's zoom animation so it scales/pans WITH the map and
// stays pinned to the ground (including smooth mobile pinch-zoom).
const BlueprintLayer = L.Layer.extend({
  initialize(params) { this._params = { ...params }; },
  setUrl(url) { this._url = url; if (this._img) this._img.src = url; return this; },
  setParams(p) { this._params = { ...this._params, ...p }; if (this._map) this._render(); return this; },
  onAdd(map) {
    this._map = map;
    if (!this._el) {
      const el = L.DomUtil.create("div", "gt-blueprint");
      el.style.position = "absolute";
      el.style.pointerEvents = "none";
      const img = L.DomUtil.create("img", "", el);
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.display = "block";
      img.draggable = false;
      img.style.userSelect = "none";
      img.style.boxShadow = "0 0 0 1px rgba(245,184,0,0.55)";
      if (this._url) img.src = this._url;
      this._el = el;
      this._img = img;
    }
    map.getPane("blueprint").appendChild(this._el);
    map.on("zoom viewreset", this._render, this);
    map.on("zoomanim", this._animateZoom, this);
    this._render();
  },
  onRemove(map) {
    if (this._el && this._el.parentNode) this._el.parentNode.removeChild(this._el);
    map.off("zoom viewreset", this._render, this);
    map.off("zoomanim", this._animateZoom, this);
  },
  _bounds() {
    const p = this._params;
    const heightM = p.widthMeters * p.aspect;
    const halfWdeg = p.widthMeters / 2 / (111320 * Math.cos((p.centerLat * Math.PI) / 180));
    const halfHdeg = heightM / 2 / 111320;
    const nw = L.latLng(p.centerLat + halfHdeg, p.centerLng - halfWdeg);
    const se = L.latLng(p.centerLat - halfHdeg, p.centerLng + halfWdeg);
    return L.latLngBounds(se, nw);
  },
  _render() {
    if (!this._map || !this._el) return;
    const p = this._params;
    if (!p.visible) { this._el.style.display = "none"; return; }
    this._el.style.display = "block";
    const b = this._bounds();
    const nw = this._map.latLngToLayerPoint(b.getNorthWest());
    const se = this._map.latLngToLayerPoint(b.getSouthEast());
    const size = se.subtract(nw);
    L.DomUtil.setPosition(this._el, nw);
    this._el.style.width = `${Math.abs(size.x)}px`;
    this._el.style.height = `${Math.abs(size.y)}px`;
    this._el.style.opacity = String(p.opacity);
    this._img.style.transform = `rotate(${p.rotationDeg}deg)`;
  },
  _animateZoom(e) {
    if (!this._map || !this._el || !this._params.visible) return;
    const b = this._bounds();
    const scale = this._map.getZoomScale(e.zoom);
    const nw = this._map._latLngToNewLayerPoint(b.getNorthWest(), e.zoom, e.center);
    L.DomUtil.setTransform(this._el, nw, scale);
  },
});

export default function MapApp() {
  const { user, logout } = useAuth();

  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const satLayerRef = useRef(null);
  const streetLayerRef = useRef(null);
  const blueprintLayerRef = useRef(null);
  const gestureElRef = useRef(null);
  const gpsMarkerRef = useRef(null);
  const coverageLayerRef = useRef(null);
  const tracksLayerRef = useRef(null);
  const measureLayerRef = useRef(null);

  const overlayRef = useRef({ ...US_CENTER, centerLat: US_CENTER.lat, centerLng: US_CENTER.lng, widthMeters: 200, rotationDeg: 0, opacity: 0.55, locked: false, aspect: 0.75, visible: true });
  const imgUrlRef = useRef(null);
  const planRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureStartRef = useRef(null);
  const saveTimerRef = useRef(null);
  const measureModeRef = useRef(false);
  const measurePtsRef = useRef([]);
  const activeTrackIdRef = useRef(null);
  const pointsBufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  const firstFixRef = useRef(false);

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [overlay, setOverlay] = useState(overlayRef.current);
  const [gps, setGps] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [measureMode, setMeasureMode] = useState(false);
  const [measureReadout, setMeasureReadout] = useState(null);
  const [baseType, setBaseType] = useState("sat");
  const [panel, setPanel] = useState(null);
  const [layersVis, setLayersVis] = useState({ blueprint: true, coverage: true, tracks: true });
  const [showOpacity, setShowOpacity] = useState(false);
  const [newProjName, setNewProjName] = useState("");
  const [uploading, setUploading] = useState(false);

  // ---------------- Blueprint reposition ----------------
  const reposition = useCallback(() => {
    const map = mapRef.current;
    const layer = blueprintLayerRef.current;
    if (!map || !layer) return;
    const o = overlayRef.current;
    if (!imgUrlRef.current) {
      if (map.hasLayer(layer)) map.removeLayer(layer);
      return;
    }
    layer.setParams(o);
    if (!map.hasLayer(layer)) layer.addTo(map);
  }, []);

  const applyOverlay = useCallback((partial, { save = true } = {}) => {
    overlayRef.current = { ...overlayRef.current, ...partial };
    setOverlay({ ...overlayRef.current });
    reposition();
    if (save && planRef.current) {
      clearTimeout(saveTimerRef.current);
      const snapshot = { ...overlayRef.current };
      saveTimerRef.current = setTimeout(() => {
        api.put(`/plans/${planRef.current.id}/overlay`, snapshot).catch(() => {});
      }, 500);
    }
  }, [reposition]);

  const commitOverlay = useCallback(() => {
    setOverlay({ ...overlayRef.current });
    if (planRef.current) {
      api.put(`/plans/${planRef.current.id}/overlay`, { ...overlayRef.current }).catch(() => {});
    }
  }, []);

  // ---------------- Gesture handling ----------------
  const snapshotGesture = useCallback(() => {
    const map = mapRef.current;
    const arr = Array.from(pointersRef.current.entries()).map(([id, p]) => ({ id, x: p.x, y: p.y }));
    gestureStartRef.current = { pointers: arr, overlay: { ...overlayRef.current } };
    void map;
  }, []);

  const applyGesture = useCallback(() => {
    const map = mapRef.current;
    const start = gestureStartRef.current;
    if (!map || !start) return;
    const cur = pointersRef.current;
    const so = start.overlay;
    if (start.pointers.length === 1) {
      const s = start.pointers[0];
      const c = cur.get(s.id);
      if (!c) return;
      const dx = c.x - s.x, dy = c.y - s.y;
      const sp = map.latLngToContainerPoint([so.centerLat, so.centerLng]);
      const ll = map.containerPointToLatLng(L.point(sp.x + dx, sp.y + dy));
      overlayRef.current = { ...overlayRef.current, centerLat: ll.lat, centerLng: ll.lng };
      reposition();
    } else if (start.pointers.length >= 2) {
      const s0 = start.pointers[0], s1 = start.pointers[1];
      const c0 = cur.get(s0.id), c1 = cur.get(s1.id);
      if (!c0 || !c1) return;
      const startDist = dist2(s0, s1) || 1;
      const curDist = dist2(c0, c1) || 1;
      const scale = curDist / startDist;
      const startAng = Math.atan2(s1.y - s0.y, s1.x - s0.x);
      const curAng = Math.atan2(c1.y - c0.y, c1.x - c0.x);
      const dRot = ((curAng - startAng) * 180) / Math.PI;
      const sMid = { x: (s0.x + s1.x) / 2, y: (s0.y + s1.y) / 2 };
      const cMid = { x: (c0.x + c1.x) / 2, y: (c0.y + c1.y) / 2 };
      const sp = map.latLngToContainerPoint([so.centerLat, so.centerLng]);
      const ll = map.containerPointToLatLng(L.point(sp.x + (cMid.x - sMid.x), sp.y + (cMid.y - sMid.y)));
      overlayRef.current = {
        ...overlayRef.current,
        widthMeters: Math.min(2_000_000, Math.max(1, so.widthMeters * scale)),
        rotationDeg: so.rotationDeg + dRot,
        centerLat: ll.lat, centerLng: ll.lng,
      };
      reposition();
    }
  }, [reposition]);

  // ---------------- GPS ----------------
  const handleFix = useCallback((pos) => {
    const { latitude, longitude, accuracy, heading, speed } = pos.coords;
    const g = { lat: latitude, lng: longitude, accuracy, heading, speed };
    setGps(g);
    const map = mapRef.current;
    if (!map) return;
    if (!gpsMarkerRef.current) {
      const icon = L.divIcon({
        className: "",
        html: `<div class="gps-dot-wrap"><div class="gps-dot-ring"></div><div class="gps-dot-core"></div></div>`,
        iconSize: [20, 20], iconAnchor: [10, 10],
      });
      gpsMarkerRef.current = L.marker([latitude, longitude], { icon, zIndexOffset: 1000, interactive: false }).addTo(map);
    } else {
      gpsMarkerRef.current.setLatLng([latitude, longitude]);
    }
    if (!firstFixRef.current) {
      firstFixRef.current = true;
      map.setView([latitude, longitude], 18);
    }
    if (activeTrackIdRef.current) {
      const pt = { lat: latitude, lng: longitude, ts: Date.now(), accuracy };
      pointsBufferRef.current.push(pt);
      L.circle([latitude, longitude], { radius: COVER_RADIUS, weight: 0, fillColor: "#F5B800", fillOpacity: 0.3 })
        .addTo(coverageLayerRef.current);
    }
  }, []);

  // ---------------- Measure ----------------
  const redrawMeasure = useCallback(() => {
    const layer = measureLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const pts = measurePtsRef.current;
    if (pts.length === 0) { setMeasureReadout(null); return; }
    const latlngs = pts.map((p) => [p.lat, p.lng]);
    if (pts.length >= 2) {
      L.polyline(latlngs, { color: "#06B6D4", weight: 3, dashArray: "6 6" }).addTo(layer);
    }
    pts.forEach((p) => {
      L.circleMarker([p.lat, p.lng], { radius: 5, color: "#06B6D4", weight: 2, fillColor: "#0D0E10", fillOpacity: 1 }).addTo(layer);
    });
    const d = pathLength(pts);
    const area = pts.length >= 3 ? polygonArea(pts) : 0;
    let toMe = null;
    if (gpsMarkerRef.current) {
      const g = gpsMarkerRef.current.getLatLng();
      toMe = haversine({ lat: g.lat, lng: g.lng }, pts[pts.length - 1]);
    }
    setMeasureReadout({ dist: d, area, toMe });
  }, []);

  // ---------------- Map init ----------------
  useEffect(() => {
    if (mapRef.current || !mapElRef.current) return;
    const map = L.map(mapElRef.current, { zoomControl: true, attributionControl: true }).setView([US_CENTER.lat, US_CENTER.lng], 4);
    mapRef.current = map;
    L.control.zoom({ position: "topright" });

    satLayerRef.current = L.tileLayer(SAT_URL, { maxZoom: 21, maxNativeZoom: 19, attribution: "Imagery © Esri" }).addTo(map);
    streetLayerRef.current = L.tileLayer(STREET_URL, { maxZoom: 21, subdomains: "abcd", attribution: "© CARTO © OSM" });

    coverageLayerRef.current = L.layerGroup().addTo(map);
    tracksLayerRef.current = L.layerGroup().addTo(map);
    measureLayerRef.current = L.layerGroup().addTo(map);

    const container = map.getContainer();
    map.createPane("blueprint");
    const bpPane = map.getPane("blueprint");
    bpPane.style.zIndex = 400;
    bpPane.style.pointerEvents = "none";
    blueprintLayerRef.current = new BlueprintLayer({ ...overlayRef.current });

    const gest = document.createElement("div");
    gest.style.cssText = "position:absolute;inset:0;z-index:450;display:none;touch-action:none;cursor:grab;";
    container.appendChild(gest);
    gestureElRef.current = gest;

    const onDown = (e) => {
      gest.setPointerCapture(e.pointerId);
      pointersRef.current.set(e.pointerId, { x: e.clientX - container.getBoundingClientRect().left, y: e.clientY - container.getBoundingClientRect().top });
      snapshotGesture();
    };
    const onMove = (e) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      const r = container.getBoundingClientRect();
      pointersRef.current.set(e.pointerId, { x: e.clientX - r.left, y: e.clientY - r.top });
      applyGesture();
    };
    const onUp = (e) => {
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size === 0) commitOverlay();
      else snapshotGesture();
    };
    const onWheel = (e) => {
      e.preventDefault();
      const o = overlayRef.current;
      if (e.shiftKey) {
        overlayRef.current = { ...o, rotationDeg: o.rotationDeg + (e.deltaY > 0 ? 3 : -3) };
      } else {
        const f = e.deltaY > 0 ? 0.95 : 1.05;
        overlayRef.current = { ...o, widthMeters: Math.max(1, o.widthMeters * f) };
      }
      reposition();
      setOverlay({ ...overlayRef.current });
      clearTimeout(saveTimerRef.current);
      const snap = { ...overlayRef.current };
      saveTimerRef.current = setTimeout(() => { if (planRef.current) api.put(`/plans/${planRef.current.id}/overlay`, snap).catch(() => {}); }, 500);
    };
    gest.addEventListener("pointerdown", onDown);
    gest.addEventListener("pointermove", onMove);
    gest.addEventListener("pointerup", onUp);
    gest.addEventListener("pointercancel", onUp);
    gest.addEventListener("wheel", onWheel, { passive: false });

    const onMapClick = (e) => {
      if (!measureModeRef.current) return;
      measurePtsRef.current = [...measurePtsRef.current, { lat: e.latlng.lat, lng: e.latlng.lng }];
      redrawMeasure();
    };
    map.on("click", onMapClick);
    map.on("resize", reposition);

    let watchId = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(handleFix, () => {}, { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 });
    }

    setTimeout(() => { map.invalidateSize(); reposition(); }, 200);

    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      clearInterval(flushTimerRef.current);
      map.remove();
      mapRef.current = null;
    };
  }, [applyGesture, snapshotGesture, commitOverlay, reposition, handleFix, redrawMeasure]);

  // Sync gesture layer visibility with lock/measure state
  useEffect(() => {
    const gest = gestureElRef.current;
    const map = mapRef.current;
    if (!gest || !map) return;
    const active = plan && imgUrlRef.current && !overlay.locked && !measureMode && overlay.visible;
    gest.style.display = active ? "block" : "none";
    if (active) { map.dragging.disable(); map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.touchZoom.disable(); }
    else { map.dragging.enable(); map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.touchZoom.enable(); }
  }, [plan, overlay.locked, overlay.visible, measureMode]);

  useEffect(() => { measureModeRef.current = measureMode; }, [measureMode]);
  useEffect(() => { activeTrackIdRef.current = activeTrackId; }, [activeTrackId]);

  // ---------------- Data loading ----------------
  const loadProjects = useCallback(async () => {
    const res = await api.get("/projects");
    setProjects(res.data);
    if (res.data.length && !planRef.current) selectProject(res.data[0]);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (user) loadProjects(); }, [user, loadProjects]);

  const drawTracks = useCallback((list) => {
    const layer = tracksLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    list.forEach((t) => {
      if (t.visible === false || !layersVis.tracks) return;
      (t.points || []).forEach((p) => {
        L.circle([p.lat, p.lng], { radius: COVER_RADIUS, weight: 0, fillColor: "#84CC16", fillOpacity: 0.28 }).addTo(layer);
      });
    });
  }, [layersVis.tracks]);

  const selectProject = async (proj) => {
    setProject(proj);
    setPanel(null);
    const [pl, tr] = await Promise.all([
      api.get(`/projects/${proj.id}/plans`),
      api.get(`/projects/${proj.id}/tracks`),
    ]);
    setPlans(pl.data);
    setTracks(tr.data);
    drawTracks(tr.data);
    if (pl.data.length) selectPlan(pl.data[0]);
    else { setPlan(null); planRef.current = null; imgUrlRef.current = null; reposition(); }
  };

  const selectPlan = async (p) => {
    setPlan(p);
    planRef.current = p;
    const o = { ...overlayRef.current, ...p.overlay };
    // If plan still at world default, drop it onto current map view
    const map = mapRef.current;
    if (map && Math.abs(o.centerLat - US_CENTER.lat) < 0.001 && Math.abs(o.centerLng - US_CENTER.lng) < 0.001) {
      const c = map.getCenter();
      o.centerLat = c.lat; o.centerLng = c.lng;
    }
    overlayRef.current = o;
    setOverlay({ ...o });
    setPanel(null);
    try {
      const res = await api.get(`/files/${p.storage_path}`, { responseType: "blob" });
      if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
      const url = URL.createObjectURL(res.data);
      imgUrlRef.current = url;
      if (blueprintLayerRef.current) blueprintLayerRef.current.setUrl(url);
      if (map && !firstFixRef.current) map.setView([o.centerLat, o.centerLng], 18);
      reposition();
    } catch {
      toast.error("Could not load blueprint image");
    }
  };

  const createProject = async () => {
    if (!newProjName.trim()) return;
    const res = await api.post("/projects", { name: newProjName.trim() });
    setNewProjName("");
    await loadProjects();
    selectProject(res.data);
    toast.success("Project created");
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !project) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", file.name.replace(/\.[^.]+$/, ""));
      fd.append("file", file);
      const res = await api.post(`/projects/${project.id}/plans`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newPlan = res.data;
      const map = mapRef.current;
      // place on current view with a sensible size
      let width = 200;
      if (map) {
        const b = map.getBounds();
        width = haversine({ lat: b.getCenter().lat, lng: b.getWest() }, { lat: b.getCenter().lat, lng: b.getEast() }) * 0.55;
        const c = map.getCenter();
        newPlan.overlay = { ...newPlan.overlay, centerLat: c.lat, centerLng: c.lng, widthMeters: width };
      }
      const pl = await api.get(`/projects/${project.id}/plans`);
      setPlans(pl.data);
      await selectPlan(newPlan);
      applyOverlay({ centerLat: newPlan.overlay.centerLat, centerLng: newPlan.overlay.centerLng, widthMeters: newPlan.overlay.widthMeters });
      toast.success("Blueprint uploaded — align it now");
      setPanel(null);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deletePlan = async (p) => {
    await api.delete(`/plans/${p.id}`);
    const pl = await api.get(`/projects/${project.id}/plans`);
    setPlans(pl.data);
    if (planRef.current?.id === p.id) {
      planRef.current = null; setPlan(null); imgUrlRef.current = null; reposition();
      if (pl.data.length) selectPlan(pl.data[0]);
    }
  };

  // ---------------- Controls ----------------
  const centerOnMe = () => {
    const map = mapRef.current;
    if (!map || !gpsMarkerRef.current) { toast.error("No GPS position yet"); return; }
    map.setView(gpsMarkerRef.current.getLatLng(), Math.max(map.getZoom(), 18));
  };

  const toggleLock = () => applyOverlay({ locked: !overlay.locked });
  const resetPlan = () => {
    const map = mapRef.current;
    const c = map ? map.getCenter() : US_CENTER;
    applyOverlay({ centerLat: c.lat, centerLng: c.lng, rotationDeg: 0, opacity: 0.55, widthMeters: 200 });
  };

  const setBase = (type) => {
    const map = mapRef.current;
    if (type === "sat") { map.removeLayer(streetLayerRef.current); satLayerRef.current.addTo(map); }
    else { map.removeLayer(satLayerRef.current); streetLayerRef.current.addTo(map); }
    satLayerRef.current.bringToBack(); streetLayerRef.current.bringToBack();
    setBaseType(type);
  };

  const startTracking = async () => {
    if (!project) { toast.error("Select a project first"); return; }
    const res = await api.post(`/projects/${project.id}/tracks`, {});
    setActiveTrackId(res.data.id);
    activeTrackIdRef.current = res.data.id;
    pointsBufferRef.current = [];
    coverageLayerRef.current.clearLayers();
    flushTimerRef.current = setInterval(async () => {
      if (!pointsBufferRef.current.length || !activeTrackIdRef.current) return;
      const batch = pointsBufferRef.current;
      pointsBufferRef.current = [];
      try { await api.put(`/tracks/${activeTrackIdRef.current}`, { points: batch }); } catch {}
    }, 5000);
    toast.success("Work tracking started");
    setTracks((t) => [res.data, ...t]);
  };

  const stopTracking = async () => {
    const id = activeTrackIdRef.current;
    clearInterval(flushTimerRef.current);
    if (pointsBufferRef.current.length && id) {
      try { await api.put(`/tracks/${id}`, { points: pointsBufferRef.current }); } catch {}
    }
    pointsBufferRef.current = [];
    if (id) { try { await api.put(`/tracks/${id}`, { active: false }); } catch {} }
    setActiveTrackId(null);
    activeTrackIdRef.current = null;
    coverageLayerRef.current.clearLayers();
    const tr = await api.get(`/projects/${project.id}/tracks`);
    setTracks(tr.data);
    drawTracks(tr.data);
    toast.success("Work tracking stopped");
  };

  const toggleTrackVisible = async (t) => {
    const updated = tracks.map((x) => (x.id === t.id ? { ...x, visible: !(x.visible !== false) } : x));
    setTracks(updated);
    setTimeout(() => drawTracks(updated), 0);
  };

  const deleteTrack = async (t) => {
    await api.delete(`/tracks/${t.id}`);
    const left = tracks.filter((x) => x.id !== t.id);
    setTracks(left);
    drawTracks(left);
  };

  const clearMeasure = () => { measurePtsRef.current = []; redrawMeasure(); };
  const toggleMeasure = () => {
    setMeasureMode((m) => {
      const nv = !m;
      if (!nv) { measurePtsRef.current = []; redrawMeasure(); }
      else { setPanel("measure"); }
      return nv;
    });
  };

  useEffect(() => {
    const layer = coverageLayerRef.current;
    if (layer) layer.getContainer && null;
    if (mapRef.current) {
      if (layersVis.coverage) coverageLayerRef.current.addTo(mapRef.current);
      else mapRef.current.removeLayer(coverageLayerRef.current);
      drawTracks(tracks);
    }
    // eslint-disable-next-line
  }, [layersVis.coverage, layersVis.tracks]);

  useEffect(() => {
    overlayRef.current.visible = layersVis.blueprint;
    reposition();
    // eslint-disable-next-line
  }, [layersVis.blueprint]);

  // ---------------- Render ----------------
  const HudBtn = ({ active, onClick, testid, Icon, label, danger }) => (
    <button onClick={onClick} data-testid={testid}
      className={`flex h-14 min-w-[64px] flex-col items-center justify-center gap-0.5 border px-3 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors
        ${active ? (danger ? "border-red-500 bg-red-500 text-black" : "border-yellow bg-yellow text-black shadow-[0_0_15px_rgba(245,184,0,0.3)]")
        : "border-edge bg-panel/90 text-gray-200 backdrop-blur-md hover:border-yellow hover:text-yellow"}`}>
      <Icon className="h-5 w-5" /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 overflow-hidden bg-void">
      <div ref={mapElRef} data-testid="map-canvas" className="absolute inset-0 z-0" />

      {/* TOP BAR */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[500] flex items-start justify-between p-3">
        <div className="pointer-events-auto flex flex-col gap-2">
          <button onClick={() => setPanel(panel === "projects" ? null : "projects")} data-testid="btn-project-select"
            className="flex items-center gap-2 border border-edge bg-panel/90 px-4 py-2.5 backdrop-blur-md">
            <FolderOpen className="h-4 w-4 text-yellow" />
            <div className="text-left">
              <div className="font-head text-sm font-bold uppercase leading-none tracking-wide text-gray-100">{project?.name || "No Project"}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{plan?.name || "No plan loaded"}</div>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </button>
          {gps && (
            <div data-testid="gps-readout" className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-edge bg-panel/90 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-gray-300 backdrop-blur-md">
              <span className="flex items-center gap-1 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" />YOU ARE HERE</span>
              <span>±{gps.accuracy ? gps.accuracy.toFixed(0) : "—"}m</span>
              {gps.heading != null && !isNaN(gps.heading) && <span>{gps.heading.toFixed(0)}°</span>}
              {gps.speed != null && !isNaN(gps.speed) && <span>{(gps.speed * 2.23694).toFixed(0)} mph</span>}
              <span className="text-gray-500">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</span>
            </div>
          )}
        </div>

        <div className="pointer-events-auto flex flex-col items-end gap-2">
          <div className="flex border border-edge bg-panel/90 backdrop-blur-md">
            <button onClick={() => setBase("sat")} data-testid="btn-base-sat" title="Satellite"
              className={`flex h-10 w-10 items-center justify-center ${baseType === "sat" ? "bg-yellow text-black" : "text-gray-300"}`}><Satellite className="h-4 w-4" /></button>
            <button onClick={() => setBase("street")} data-testid="btn-base-street" title="Map"
              className={`flex h-10 w-10 items-center justify-center ${baseType === "street" ? "bg-yellow text-black" : "text-gray-300"}`}><MapIcon className="h-4 w-4" /></button>
          </div>
          <button onClick={logout} data-testid="btn-logout"
            className="flex h-10 items-center gap-2 border border-edge bg-panel/90 px-3 font-mono text-[10px] font-bold uppercase tracking-wider text-gray-300 backdrop-blur-md hover:text-yellow">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* SURVEY DISCLAIMER */}
      <div className="pointer-events-none absolute left-1/2 top-3 z-[490] hidden -translate-x-1/2 md:block">
        <span className="border border-edge bg-panel/80 px-3 py-1 font-mono text-[9px] uppercase tracking-widest text-gray-500 backdrop-blur-md">
          Phone GPS is approximate · Not survey-grade
        </span>
      </div>

      {/* OPACITY */}
      {showOpacity && plan && (
        <div className="absolute bottom-24 left-1/2 z-[510] w-[min(92vw,420px)] -translate-x-1/2 border border-edge bg-panel/95 p-4 backdrop-blur-md" data-testid="opacity-panel">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-yellow">Blueprint Opacity</span>
            <span className="font-mono text-xs text-gray-300">{Math.round(overlay.opacity * 100)}%</span>
          </div>
          <Slider data-testid="slider-opacity" value={[overlay.opacity * 100]} min={0} max={100} step={1}
            onValueChange={(v) => applyOverlay({ opacity: v[0] / 100 }, { save: false })}
            onValueCommit={commitOverlay} className="mt-4" />
          <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-gray-600"><span>0%</span><span>50%</span><span>100%</span></div>
        </div>
      )}

      {/* BOTTOM CONTROL BAR */}
      <div className="absolute inset-x-0 bottom-0 z-[500] border-t border-edge bg-void/85 backdrop-blur-md">
        <div className="flex items-stretch gap-2 overflow-x-auto p-2">
          <HudBtn testid="btn-center-on-me" onClick={centerOnMe} Icon={Crosshair} label="Center" />
          <HudBtn testid="btn-lock-plan" onClick={toggleLock} Icon={overlay.locked ? Lock : Unlock} label={overlay.locked ? "Locked" : "Unlock'd"} active={overlay.locked} danger />
          <HudBtn testid="btn-opacity" onClick={() => setShowOpacity((s) => !s)} Icon={Gauge} label="Opacity" active={showOpacity} />
          <HudBtn testid="btn-layers-panel" onClick={() => setPanel(panel === "layers" ? null : "layers")} Icon={Layers} label="Layers" active={panel === "layers"} />
          <HudBtn testid="btn-measure-tool" onClick={toggleMeasure} Icon={Ruler} label="Measure" active={measureMode} />
          {activeTrackId ? (
            <HudBtn testid="btn-stop-tracking" onClick={stopTracking} Icon={Square} label="Stop Trk" active danger />
          ) : (
            <HudBtn testid="btn-start-tracking" onClick={startTracking} Icon={Play} label="Track" />
          )}
          <HudBtn testid="btn-plans-panel" onClick={() => setPanel(panel === "plans" ? null : "plans")} Icon={FolderOpen} label="Plans" active={panel === "plans"} />
          <HudBtn testid="btn-reset-plan" onClick={resetPlan} Icon={RotateCcw} label="Reset" />
        </div>
      </div>

      {/* SLIDE-UP PANEL */}
      {panel && (
        <div className="absolute inset-x-0 bottom-[76px] z-[520] max-h-[60vh] overflow-y-auto border-t-2 border-yellow bg-panel shadow-[0_-10px_40px_rgba(0,0,0,0.8)]" data-testid={`panel-${panel}`}>
          <div className="flex items-center justify-between border-b border-edge px-4 py-3">
            <span className="font-head text-lg font-bold uppercase tracking-wide text-gray-100">
              {panel === "projects" && "Projects"}
              {panel === "plans" && "Plan Sheets"}
              {panel === "layers" && "Map Layers"}
              {panel === "tracking" && "Work Tracking"}
              {panel === "measure" && "Field Measure"}
            </span>
            <button onClick={() => setPanel(null)} data-testid="btn-close-panel" className="text-gray-400 hover:text-yellow"><X className="h-5 w-5" /></button>
          </div>

          <div className="p-4">
            {panel === "projects" && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input value={newProjName} onChange={(e) => setNewProjName(e.target.value)} placeholder="NEW PROJECT NAME"
                    data-testid="input-project-name"
                    className="flex-1 border border-edge bg-inputbg px-3 py-2.5 font-mono text-sm uppercase tracking-wide text-gray-100 placeholder:text-gray-600 focus:border-yellow focus:outline-none" />
                  <button onClick={createProject} data-testid="btn-create-project"
                    className="flex items-center gap-1 bg-yellow px-4 font-head text-sm font-bold uppercase text-black hover:bg-yellow-hover"><Plus className="h-4 w-4" />Add</button>
                </div>
                <div className="space-y-2">
                  {projects.length === 0 && <p className="py-6 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No projects yet — create one above</p>}
                  {projects.map((p) => (
                    <button key={p.id} onClick={() => selectProject(p)} data-testid={`project-item-${p.id}`}
                      className={`flex w-full items-center justify-between border px-4 py-3 text-left ${project?.id === p.id ? "border-yellow bg-cardbg" : "border-edge bg-cardbg/40 hover:border-gray-600"}`}>
                      <span className="font-head text-base font-bold uppercase tracking-wide text-gray-100">{p.name}</span>
                      {project?.id === p.id && <span className="font-mono text-[10px] uppercase tracking-widest text-yellow">Active</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {panel === "plans" && (
              <div className="space-y-4">
                {!project && <p className="py-6 text-center font-mono text-xs uppercase tracking-widest text-gray-500">Select a project first</p>}
                {project && (
                  <>
                    <label className={`flex cursor-pointer items-center justify-center gap-2 border-2 border-dashed border-edge bg-cardbg/40 py-6 font-head text-sm font-bold uppercase tracking-wider text-gray-300 hover:border-yellow hover:text-yellow ${uploading ? "opacity-50" : ""}`} data-testid="upload-plan-label">
                      <Upload className="h-5 w-5" /> {uploading ? "Uploading…" : "Upload Blueprint (PDF / JPG / PNG)"}
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,image/*" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="upload-plan-input" />
                    </label>
                    <div className="space-y-2">
                      {plans.map((p) => (
                        <div key={p.id} className={`flex items-center justify-between border px-4 py-3 ${plan?.id === p.id ? "border-yellow bg-cardbg" : "border-edge bg-cardbg/40"}`}>
                          <button onClick={() => selectPlan(p)} data-testid={`plan-item-${p.id}`} className="flex-1 text-left">
                            <div className="font-head text-base font-bold uppercase tracking-wide text-gray-100">{p.name}</div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{p.original_filename}</div>
                          </button>
                          <button onClick={() => deletePlan(p)} data-testid={`plan-delete-${p.id}`} className="text-gray-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      {plans.length === 0 && <p className="py-4 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No plan sheets yet</p>}
                    </div>
                  </>
                )}
              </div>
            )}

            {panel === "layers" && (
              <div className="space-y-2">
                {[
                  { key: "blueprint", label: "Blueprint Overlay" },
                  { key: "coverage", label: "Live Coverage" },
                  { key: "tracks", label: "Historical Tracks" },
                ].map((l) => (
                  <button key={l.key} onClick={() => setLayersVis((v) => ({ ...v, [l.key]: !v[l.key] }))}
                    data-testid={`layer-toggle-${l.key}`}
                    className="flex w-full items-center justify-between border border-edge bg-cardbg/40 px-4 py-3">
                    <span className="font-head text-base font-bold uppercase tracking-wide text-gray-200">{l.label}</span>
                    {layersVis[l.key] ? <Eye className="h-5 w-5 text-yellow" /> : <EyeOff className="h-5 w-5 text-gray-600" />}
                  </button>
                ))}
                <p className="pt-2 font-mono text-[10px] uppercase leading-relaxed tracking-widest text-gray-600">
                  Satellite · Street · Elevation layers switch from the top-right toggle.
                </p>
              </div>
            )}

            {panel === "measure" && (
              <div className="space-y-4">
                <p className="font-mono text-xs uppercase leading-relaxed tracking-wide text-gray-400">
                  Tap the map to drop points. Distance follows the path; area needs 3+ points.
                </p>
                {measureReadout ? (
                  <div className="grid grid-cols-3 gap-px border border-edge bg-edge">
                    <div className="bg-cardbg p-3"><div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Distance</div><div className="mt-1 font-mono text-lg font-bold text-yellow">{fmtDist(measureReadout.dist)}</div></div>
                    <div className="bg-cardbg p-3"><div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Area</div><div className="mt-1 font-mono text-lg font-bold text-yellow">{measureReadout.area ? fmtArea(measureReadout.area) : "—"}</div></div>
                    <div className="bg-cardbg p-3"><div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">From You</div><div className="mt-1 font-mono text-lg font-bold text-emerald-400">{measureReadout.toMe != null ? fmtDist(measureReadout.toMe) : "—"}</div></div>
                  </div>
                ) : <p className="font-mono text-xs uppercase tracking-widest text-gray-600">No points dropped</p>}
                <button onClick={clearMeasure} data-testid="btn-clear-measure" className="w-full border border-edge py-3 font-head text-sm font-bold uppercase tracking-wider text-gray-300 hover:border-yellow hover:text-yellow">Clear Points</button>
              </div>
            )}

            {panel === "tracking" && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {activeTrackId ? (
                    <button onClick={stopTracking} className="flex flex-1 items-center justify-center gap-2 bg-red-500 py-3 font-head font-bold uppercase text-black"><Square className="h-4 w-4" />Stop Tracking</button>
                  ) : (
                    <button onClick={startTracking} className="flex flex-1 items-center justify-center gap-2 bg-yellow py-3 font-head font-bold uppercase text-black"><Play className="h-4 w-4" />Start Tracking</button>
                  )}
                </div>
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-widest text-gray-600">
                  Off by default. Driving with tracking OFF does not modify coverage.
                </p>
                <div className="space-y-2">
                  {tracks.map((t) => (
                    <div key={t.id} className="flex items-center justify-between border border-edge bg-cardbg/40 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Route className="h-4 w-4 text-yellow" />
                        <div>
                          <div className="font-head text-sm font-bold uppercase tracking-wide text-gray-100">{t.name}{t.active && <span className="ml-2 text-emerald-400">● LIVE</span>}</div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{(t.points || []).length} pts · {t.operator}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleTrackVisible(t)} data-testid={`track-visible-${t.id}`}>{t.visible === false ? <EyeOff className="h-4 w-4 text-gray-600" /> : <Eye className="h-4 w-4 text-yellow" />}</button>
                        <button onClick={() => deleteTrack(t)} data-testid={`track-delete-${t.id}`}><Trash2 className="h-4 w-4 text-gray-500 hover:text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                  {tracks.length === 0 && <p className="py-4 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No sessions recorded</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unlock hint */}
      {plan && !overlay.locked && !measureMode && (
        <div className="pointer-events-none absolute left-1/2 top-20 z-[490] -translate-x-1/2 md:top-14">
          <span className="border border-yellow/60 bg-void/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-yellow backdrop-blur-md">
            Drag · Pinch · Rotate to align — then Lock
          </span>
        </div>
      )}
    </div>
  );
}
